import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsAPI } from '../../utils/api';

const pick = (settings, key, fallback = '') =>
  (settings[key] ?? fallback) || fallback;

const ContactPage = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const { i18n, t } = useTranslation();
  const isAr = i18n.language === 'ar';
  const suf = isAr ? '_ar' : '';

  useEffect(() => {
    settingsAPI.getPublic()
      .then(rows => setSettings(Object.fromEntries((rows || []).map(r => [r.setting_key, r.setting_value]))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div className="spinner" />
    </div>
  );

  const v = (key, fallback) => pick(settings, key + suf, pick(settings, key, fallback));

  const title = v('contact_title', isAr ? 'تواصل معنا' : 'Contact Us');
  const intro = v('contact_intro', isAr
    ? 'يسعدنا تواصلك معنا. اتصل بنا أو راسلنا في أي وقت.'
    : "We'd love to hear from you. Reach out anytime by phone or email.");

  const phone = pick(settings, 'contact_phone', '');
  const email = pick(settings, 'contact_email', '');
  const address = v('contact_address', '');
  const whatsapp = pick(settings, 'contact_whatsapp', phone);
  const hours = v('contact_hours', '');
  const instagram = pick(settings, 'social_instagram', '');

  // Sanitize phone for tel: / wa.me links (strip everything but + and digits)
  const telHref = (p) => p ? `tel:${p.replace(/[^+\d]/g, '')}` : null;
  const waHref = (p) => p ? `https://wa.me/${p.replace(/[^\d]/g, '')}` : null;

  const items = [
    phone && {
      label: isAr ? 'الهاتف' : 'Phone',
      value: phone,
      href: telHref(phone),
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
        </svg>
      ),
    },
    whatsapp && {
      label: 'WhatsApp',
      value: whatsapp,
      href: waHref(whatsapp),
      external: true,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
    },
    email && {
      label: isAr ? 'البريد الإلكتروني' : 'Email',
      value: email,
      href: `mailto:${email}`,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
      ),
    },
    address && {
      label: isAr ? 'العنوان' : 'Address',
      value: address,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      ),
    },
    hours && {
      label: isAr ? 'ساعات العمل' : 'Working Hours',
      value: hours,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
    },
    instagram && {
      label: 'Instagram',
      value: instagram.replace(/^https?:\/\/(www\.)?instagram\.com\//, '@').replace(/\/$/, ''),
      href: instagram.startsWith('http') ? instagram : `https://instagram.com/${instagram.replace(/^@/, '')}`,
      external: true,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
          <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/>
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
        </svg>
      ),
    },
  ].filter(Boolean);

  return (
    <div className="contact-page" dir={isAr ? 'rtl' : 'ltr'}>
      <section className="contact-hero">
        <div className="container">
          <h1>{title}</h1>
          {intro && <p>{intro}</p>}
        </div>
      </section>

      <section className="container contact-cards">
        {items.map((item, i) => {
          const inner = (
            <>
              <div className="contact-card__icon">{item.icon}</div>
              <div className="contact-card__body">
                <div className="contact-card__label">{item.label}</div>
                <div className="contact-card__value">{item.value}</div>
              </div>
            </>
          );
          if (item.href) {
            return (
              <a
                key={i}
                href={item.href}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noreferrer' : undefined}
                className="contact-card"
              >
                {inner}
              </a>
            );
          }
          return <div key={i} className="contact-card">{inner}</div>;
        })}
      </section>

      <style>{`
        .contact-page { padding: 30px 0 80px; }
        .contact-hero { padding: 30px 0 40px; text-align: center; }
        .contact-hero h1 {
          font-family: var(--font-serif); font-size: 2.6rem;
          color: var(--olive, #4a5e2a); margin-bottom: 16px;
        }
        .contact-hero p {
          color: var(--text-muted); font-size: 1.05rem;
          line-height: 1.8; max-width: 560px; margin: 0 auto;
        }
        .contact-cards {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 18px; margin-top: 30px;
        }
        .contact-card {
          background: var(--white); border-radius: var(--radius-lg);
          padding: 24px; display: flex; gap: 16px; align-items: flex-start;
          text-decoration: none; color: inherit;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }
        a.contact-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md, 0 8px 24px rgba(0,0,0,0.08));
        }
        .contact-card__icon {
          flex-shrink: 0; width: 44px; height: 44px;
          border-radius: 50%; background: var(--cream);
          color: var(--olive, #4a5e2a);
          display: flex; align-items: center; justify-content: center;
        }
        .contact-card__label {
          font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.06em;
          color: var(--text-muted); margin-bottom: 4px;
        }
        .contact-card__value {
          font-size: 1.02rem; color: var(--text);
          word-break: break-word; line-height: 1.5;
        }
        @media (max-width: 600px) {
          .contact-hero h1 { font-size: 2rem; }
          .contact-cards { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default ContactPage;
